import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Login as LoginIcon,
  OpenInNew as OpenInNewIcon,
  MarkEmailRead as MarkEmailReadIcon,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { REGISTER_COMPANY_WITH_ADMIN } from '../graphql/mutations';
import { GET_PUBLIC_SITE_SETTINGS } from '../graphql/queries';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    eik: '',
    promoCode: '',
    acceptTerms: false,
  });
  const [error, setError] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [generatedUsername, setGeneratedUsername] = useState('');

  // Fetch public site settings
  const { data: siteSettingsData } = useQuery(GET_PUBLIC_SITE_SETTINGS);
  const siteSettings = siteSettingsData?.publicSiteSettings;

  const [registerCompany, { loading }] = useMutation(REGISTER_COMPANY_WITH_ADMIN, {
    onCompleted: (data) => {
      if (data.registerCompanyWithAdmin?.success) {
        const result = data.registerCompanyWithAdmin;

        // Check if we have a token (old flow with auto-login) or not (new flow with email verification)
        if (result.token) {
          // Old flow - direct login (shouldn't happen with new backend)
          localStorage.setItem('token', result.token);
          localStorage.setItem('userData', JSON.stringify({
            userId: result.user.id,
            username: result.user.username,
            email: result.user.email,
            role: result.user.role,
            companyId: result.company.id,
            companyName: result.company.name,
          }));
          window.location.href = '/';
        } else {
          // New flow - show pending verification message
          setRegistrationSuccess(true);
          setSuccessMessage(result.message);
          if (result.user?.username) {
            setGeneratedUsername(result.user.username);
          }
        }
      } else {
        setError(data.registerCompanyWithAdmin?.message || 'Грешка при регистрация');
      }
    },
    onError: (error) => {
      console.error('Registration error:', error);
      setError(error.message || 'Грешка при регистрация');
    },
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.email || !formData.password || !formData.companyName) {
      setError('Моля попълнете всички задължителни полета');
      return;
    }

    if (formData.password.length < 5) {
      setError('Паролата трябва да е поне 5 символа');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Паролите не съвпадат');
      return;
    }

    if (!formData.acceptTerms) {
      setError('Трябва да приемете общите условия');
      return;
    }

    try {
      await registerCompany({
        variables: {
          input: {
            email: formData.email.trim(),
            password: formData.password,
            companyName: formData.companyName.trim(),
            eik: formData.eik.trim() || null,
            promoCode: formData.promoCode.trim() || null,
            acceptTerms: formData.acceptTerms,
          },
        },
      });
    } catch (err) {
      // Error handled in onError callback
    }
  };

  // Show success screen after registration
  if (registrationSuccess) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: '100%', textAlign: 'center' }}>
            <MarkEmailReadIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography component="h1" variant="h4" color="success.main" gutterBottom>
              Регистрацията е успешна!
            </Typography>

            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              {successMessage || 'Изпратен е имейл за потвърждение на адреса.'}
            </Alert>

            {generatedUsername && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body1">
                  Вашето потребителско име: <strong>{generatedUsername}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Запазете го за бъдеща употреба!
                </Typography>
              </Box>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Моля, проверете вашата поща и кликнете на линка за потвърждение,
              за да активирате акаунта си.
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Button
              variant="contained"
              startIcon={<LoginIcon />}
              onClick={() => navigate('/')}
              fullWidth
            >
              Към страницата за вход
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 4,
          marginBottom: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            {siteSettings?.siteName || 'Bash Inv'}
          </Typography>
          <Typography component="h2" variant="h6" align="center" gutterBottom sx={{ color: 'text.secondary', mb: 3 }}>
            <PersonAddIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Регистрация
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email адрес"
              name="email"
              type="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleInputChange}
              disabled={loading}
              helperText="Ще се използва за вход в системата"
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Парола"
              type="password"
              id="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleInputChange}
              disabled={loading}
              helperText="Минимум 5 символа"
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Потвърди парола"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              disabled={loading}
            />

            <Divider sx={{ my: 3 }} />

            <TextField
              margin="normal"
              required
              fullWidth
              id="companyName"
              label="Име на фирмата"
              name="companyName"
              value={formData.companyName}
              onChange={handleInputChange}
              disabled={loading}
            />

            <TextField
              margin="normal"
              fullWidth
              id="eik"
              label="ЕИК (опционално)"
              name="eik"
              value={formData.eik}
              onChange={handleInputChange}
              disabled={loading}
              helperText="Единен идентификационен код (9-13 цифри)"
            />

            <TextField
              margin="normal"
              fullWidth
              id="promoCode"
              label="Промокод (опционално)"
              name="promoCode"
              value={formData.promoCode}
              onChange={handleInputChange}
              disabled={loading}
              helperText="Ако имате промокод от наш партньор"
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleInputChange}
                  disabled={loading}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  Съгласен съм с{' '}
                  {siteSettings?.termsUrl ? (
                    <Link href={siteSettings.termsUrl} target="_blank" rel="noopener noreferrer">
                      общите условия <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} />
                    </Link>
                  ) : (
                    'общите условия'
                  )}
                </Typography>
              }
              sx={{ mt: 2 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PersonAddIcon />}
            >
              {loading ? 'Регистрация...' : 'Регистрирай се'}
            </Button>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Вече имате акаунт?
              </Typography>
              <Button
                component={RouterLink}
                to="/login"
                startIcon={<LoginIcon />}
                sx={{ mt: 1 }}
              >
                Вход в системата
              </Button>
            </Box>
          </Box>

          {/* Service Description from Site Settings */}
          {siteSettings?.serviceDescription && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Информация за услугата
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                  {siteSettings.serviceDescription}
                </Typography>
                {siteSettings?.servicePrice && (
                  <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: 'bold' }}>
                    Цена: {siteSettings.servicePrice}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default Register;
